import React from 'react'
import moment from 'moment'
import { IParameterDef, IParameterValues, IResolvedDefaultMeta } from 'interfaces/IFleetMonitoring'
import strings from 'strings'
import styles from './inheritedDefaultsSummary.module.scss'

const FM = strings.FLEET_MONITORING

interface IInheritedDefaultsSummaryProps {
  defs: IParameterDef[]
  inheritedValue?: IParameterValues
  inheritedMeta?: Record<string, IResolvedDefaultMeta>
}

const provenance = (meta?: IResolvedDefaultMeta): string | undefined => {
  if (!meta || !meta.asOf) return undefined
  const asOf = moment(meta.asOf)
  if (!asOf.isValid()) return undefined
  const date = asOf.format('MMM D, YYYY')
  return meta.label
    ? FM.PARAMETERS.LAST_UPDATED_LABELED.replace('{label}', meta.label).replace('{date}', date)
    : FM.PARAMETERS.LAST_UPDATED.replace('{date}', date)
}

// Read-only view of derived-default params (those with a default_source, e.g. the expected vehicle
// count ← policy) for a fleet on Inherit. Surfaces the value the runner will actually use, and
// flags when none is resolvable so size monitoring isn't silently inactive. Renders nothing for
// validation types that have no derived defaults.
const InheritedDefaultsSummary: React.FunctionComponent<IInheritedDefaultsSummaryProps> = ({
  defs,
  inheritedValue,
  inheritedMeta,
}) => {
  const derived = (defs || []).filter(d => d.defaultSource)
  if (derived.length === 0) return null
  return (
    <div className={styles.summary} data-testid="inherited-defaults-summary">
      <span className={styles.title}>{FM.PARAMETERS.INHERITED_TITLE}</span>
      {derived.map(def => {
        const raw = inheritedValue ? inheritedValue[def.paramKey] : undefined
        const hasValue = raw !== undefined && raw !== null && String(raw) !== ''
        const hint = hasValue ? provenance(inheritedMeta && inheritedMeta[def.paramKey]) : undefined
        return (
          <div key={def.paramKey} className={styles.item}>
            <div className={styles.row}>
              <span className={styles.label}>{def.displayName ?? def.paramKey}:</span>
              <span className={hasValue ? styles.value : styles.placeholder}>
                {hasValue ? String(raw) : '—'}
              </span>
            </div>
            {!hasValue && <span className={styles.empty}>{FM.PARAMETERS.INHERITED_EMPTY}</span>}
            {hint && <span className={styles.hint}>{hint}</span>}
          </div>
        )
      })}
    </div>
  )
}

export default InheritedDefaultsSummary
